/**
 * frontend/src/pages/valuation/_OverviewParticulars.tsx
 *
 * VAL-OVERVIEW-1C R5 — the ten VS Section 100 particulars, read-and-jump.
 * Values already live on the engagement fetched by the parent — no second
 * fetch. This block has no editor of its own (VAL-INTAKE-TUNING-SPLIT-1 is
 * the defect two editors on one row repeats).
 *
 * VAL-PATH-COMPANY (P3) V3: I1-I9 are edited on step 1 (Company), I10-I11 on
 * Sign (VAL-PATH-P7c); the "Edit particulars" link to Intake is gone — read-only here.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ValuationEngagement } from '@/types/valuation'
import { STANDARDS_OF_VALUE } from './_IntakeParticulars'

const PARTICULARS_FIELDS: Array<{ field: keyof ValuationEngagement; label: string }> = [
  { field: 'prepared_for', label: 'Prepared for' },
  { field: 'purpose', label: 'Purpose of the engagement' },
  { field: 'intended_users', label: 'Intended users' },
  { field: 'standard_of_value', label: 'Standard of value' },
  { field: 'premise_of_value', label: 'Premise of value' },
  { field: 'subject_interest', label: 'Subject interest' },
  { field: 'interest_characteristics', label: 'Interest characteristics' },
  { field: 'sources_relied_upon', label: 'Sources relied upon' },
  { field: 'analyst_has_financial_interest', label: 'Analyst has a financial interest' },
  { field: 'fee_is_contingent', label: 'Fee is contingent' },
]

const BOOLEAN_FIELDS = new Set<keyof ValuationEngagement>([
  'analyst_has_financial_interest',
  'fee_is_contingent',
])

function displayValue(field: keyof ValuationEngagement, value: unknown): { text: string; unset: boolean } {
  if (BOOLEAN_FIELDS.has(field)) {
    if (typeof value !== 'boolean') return { text: 'Not set', unset: true }
    return { text: value ? 'Yes' : 'No', unset: false }
  }
  if (value === null || value === undefined || value === '') return { text: 'Not set', unset: true }
  if (field === 'standard_of_value') {
    return { text: STANDARDS_OF_VALUE.find(o => o.value === value)?.label ?? String(value), unset: false }
  }
  return { text: String(value), unset: false }
}

export function OverviewParticulars({ engagement }: { engagement: ValuationEngagement }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Particulars</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {PARTICULARS_FIELDS.map(({ field, label }) => {
            const { text, unset } = displayValue(field, engagement[field])
            return (
              <div key={field} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-3">
                <dt className="text-sm text-pal-ink-muted">{label}</dt>
                <dd className={`text-sm sm:text-right ${unset ? 'text-destructive' : 'text-gray-900'}`}>
                  {text}
                </dd>
              </div>
            )
          })}
        </dl>
      </CardContent>
    </Card>
  )
}
